import {
  BarChartOutlined,
  DownloadOutlined,
  ReloadOutlined,
  SearchOutlined,
} from '@ant-design/icons';
import { Button, Form, Input, Space, Table, Tooltip, Typography } from 'antd';
import React, { useCallback, useMemo, useRef, useState } from 'react';
import { shallowEqual, useSelector } from 'react-redux';
import settings from '../../../settings';
import { useManyRemote } from '../../../shared/use-many-remote';
import { allJoonDeviceLogsQuery, kibanaUrlQuery } from '../constants';
import { Link } from 'react-router-dom';
import { useQuery } from '@apollo/client';

const { Title, Text } = Typography;

function JoonDeviceLogs({ joonDevice }) {
  const [deleting, setDeleting] = useState(null);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: settings.pageSize,
  });
  const [form] = Form.useForm();

  const { data: kibanaData } = useQuery(kibanaUrlQuery, {
    variables: {},
    fetchPolicy: 'network-only',
  });
  const kibanaUrl = kibanaData?.kibanaUrl;

  const initialFilters = useMemo(() => {
    const fromDate = new Date();
    fromDate.setDate(fromDate.getDate() - 7);
    const untilDate = new Date();
    untilDate.setDate(untilDate.getDate() + 1);
    return {
      parentId: joonDevice ? joonDevice._id : undefined,
      type: 'joon-device-log',
    };
  }, [joonDevice]);

  const sortBy = useRef([{ key: 'updatedAt', order: 'ASC' }]);

  const filters = useRef(initialFilters);
  const {
    error,
    loading,
    data: files,
    refetch,
    search: filesSearch,
    hasNextPage,
    next,
  } = useManyRemote(
    allJoonDeviceLogsQuery,
    (data) => data.allFiles,
    settings.querySize,
    filters.current,
    sortBy.current,
  );

  const locale = useSelector((store) => store.locale, shallowEqual);

  const handleTableChange = useCallback((params) => {
    setPagination({
      ...params,
    });
  }, []);

  const onSearchChange = useCallback(
    (_changed) => {
      const { search, ...changed } = _changed;
      if (search != undefined) {
        sortBy.current = [
          { key: '_score', order: 'DESC' },
          { key: 'updatedAt', order: 'ASC' },
        ];
        filesSearch(search);
      } else {
        filters.current = {
          ...filters.current,
          ...Object.entries(changed).reduce((prev, [k, v]) => {
            prev[k] = v ? v : undefined;
            return prev;
          }, {}),
        };
        refetch();
      }
    },
    [refetch, filesSearch],
  );
  return (
    <div style={{ paddingLeft: 1, paddingRight: 1 }}>
      {error && (
        <div className="errors">
          <Text type="danger">{error}</Text>
        </div>
      )}
      <div className="top-actions">
        <Form
          layout="vertical"
          form={form}
          style={{ maxWidth: 500, minWidth: 300 }}
          onValuesChange={onSearchChange}
        >
          <Form.Item name="search">
            <Input placeholder="Search" suffix={<SearchOutlined />} />
          </Form.Item>
        </Form>
        <div>
          <a
            href={`${kibanaUrl}/app/discover#/?_g=(filters:!(),refreshInterval:(pause:!t,value:0),time:(from:now-60m,to:now))&_a=(columns:!(tag,extras.imei,msg),filters:!(),grid:(columns:(extras.imei:(width:213.66666666666663),source:(width:263),tag:(width:254.66666666666669))),index:baa082e5-379a-4169-ade5-0892bf4f784a,interval:auto,query:(language:kuery,query:'extras.imei%20:%20${joonDevice._id}'),sort:!(!(timestampMs,desc)))`}
            target="_blank"
            rel="noreferrer"
            style={{ marginRight: 16 }}
          >
            <Button icon={<BarChartOutlined />}>Kibana Logs</Button>
          </a>
          <Button icon={<ReloadOutlined />} onClick={refetch}>
            Refresh
          </Button>
        </div>
      </div>
      <Table
        dataSource={files}
        loading={!files.length && loading}
        pagination={{ ...pagination, total: files.length }}
        onChange={handleTableChange}
        style={{ width: '100%' }}
        rowKey="_id"
      >
        <Table.Column title="IMEI" dataIndex="parentId" key="parentId" />
        <Table.Column title="Filename" key="filename" dataIndex="filename" />
        {/* <Table.Column
          title="Tags"
          dataIndex="tags"
          render={(text, record) => {
            return (
              record.tags &&
              record.tags.map((tag) => (
                <Tag key={tag} color={settings.colors.secondary}>
                  {tag}
                </Tag>
              ))
            );
          }}
        /> */}
        <Table.Column
          title="Created"
          dataIndex="createdAt"
          render={(text, record) => {
            return (
              record.createdAt &&
              new Intl.DateTimeFormat(locale, {
                dateStyle: 'short',
                timeStyle: 'short',
              }).format(new Date(record.createdAt))
            );
          }}
        />
        <Table.Column
          title="Action"
          key="action"
          render={(text, record) => (
            <Space size="middle">
              {record.url && (
                <Tooltip title="Download">
                  <a key="download" href={record.url}>
                    <Button icon={<DownloadOutlined />} />
                  </a>
                </Tooltip>
              )}
              {/* <Tooltip title="Delete">
                <Button icon={<DeleteOutlined />} />
              </Tooltip> */}
            </Space>
          )}
        />
      </Table>
      <div style={{ textAlign: 'center' }}>
        {hasNextPage && (
          <Button style={{ minWidth: 200 }} onClick={next} loading={loading}>
            Load More
          </Button>
        )}
      </div>
      <style jsx>{`
        .errors {
          margin-bottom: 16px;
          text-align: center;
        }
        .top-actions {
          display: flex;
          justify-content: space-between;
          width: 100%;
          margin-bottom: 16px;
          flex-wrap: wrap;
        }
      `}</style>
    </div>
  );
}

export default JoonDeviceLogs;
